import { useEffect, useState, type FormEvent } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  api,
  FalhaApi,
  type StatusTemplate,
  type TemplateApi,
  type TemplateItemApi,
  type TemplateSecaoApi,
  type TipoItemTemplate,
} from "../api";
import { Layout } from "../componentes/Layout";

const RETULO_TIPO_ITEM: Record<TipoItemTemplate, string> = {
  CHECKBOX_SIMPLES: "Checkbox simples",
  STATUS_MULTIPLO: "Status (múltipla escolha)",
  TEXTO_LIVRE: "Texto livre",
  SIM_NAO: "Sim e Não",
  CONFORME: "Conforme / Não conforme / N.A.",
};

const RETULO_STATUS: Record<StatusTemplate, string> = {
  RASCUNHO: "Rascunho",
  PUBLICADO: "Publicado",
  ARQUIVADO: "Arquivado",
};

function resumoAvaliacao(item: TemplateItemApi) {
  if (item.avaliacao?.modo === "MARCACAO_MANUAL") return "O vistoriador decide se há problema";
  if (item.avaliacao?.modo === "OPCOES" && item.avaliacao.valoresProblema?.length) {
    return `${item.avaliacao.valoresProblema.map((v) => `“${v}”`).join(" e ")} ${item.avaliacao.valoresProblema.length > 1 ? "geram" : "gera"} pendência`;
  }
  if (item.tipo === "CONFORME") return "“Não conforme” gera pendência";
  return "Sem identificação automática de problema";
}

/**
 * Tela admin crua de edição de template (Fase 3): seções/itens são editáveis tanto em RASCUNHO
 * quanto em PUBLICADO — só corrigir um erro de digitação não deveria exigir "Nova versão"
 * (11/08: template imutável após publicar foi reportado como "muito ruim" no uso real). Seguro
 * porque a vistoria nunca lê o template ao vivo, sempre o `templateSnapshot` congelado na criação.
 * "Nova versão" continua existindo pra mudanças estruturais maiores.
 */
export function TemplateDetalhe() {
  const { id } = useParams<{ id: string }>();
  const navegar = useNavigate();
  const [template, setTemplate] = useState<TemplateApi | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [processando, setProcessando] = useState(false);
  const [editandoNome, setEditandoNome] = useState(false);
  const [nome, setNome] = useState("");

  function carregar() {
    if (!id) return;
    api
      .obterTemplate(id)
      .then(({ template }) => setTemplate(template))
      .catch(() => setErro("Falha ao carregar modelo"));
  }

  useEffect(carregar, [id]);

  async function publicar() {
    if (!template) return;
    setProcessando(true);
    try {
      await api.publicarTemplate(template.id);
      carregar();
    } catch (e) {
      setErro(e instanceof FalhaApi ? e.message : "Falha ao publicar");
    } finally {
      setProcessando(false);
    }
  }

  async function duplicar() {
    if (!template) return;
    setProcessando(true);
    try {
      const { template: novo } = await api.duplicarTemplate(template.id);
      navegar(`/templates/${novo.id}`);
    } catch (e) {
      setErro(e instanceof FalhaApi ? e.message : "Falha ao duplicar modelo");
      setProcessando(false);
    }
  }

  if (!template) {
    return (
      <Layout>
        <main className="container">
          {erro ? (
            <div className="alerta alerta-erro" role="alert">
              {erro}
            </div>
          ) : (
            <p>Carregando…</p>
          )}
        </main>
      </Layout>
    );
  }

  const editavel = template.status !== "ARQUIVADO";

  async function salvarNome() {
    if (!nome.trim()) return;
    setProcessando(true);
    try { await api.editarTemplate(template!.id, { nome: nome.trim() }); setEditandoNome(false); carregar(); }
    catch (e) { setErro(e instanceof FalhaApi ? e.message : "Falha ao salvar nome"); }
    finally { setProcessando(false); }
  }

  return (
    <Layout>
      <main className="container">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "start" }}>
          <div>
            {editandoNome ? <div className="modelo-editar-nome">
              <input value={nome} onChange={(e) => setNome(e.target.value)} autoFocus aria-label="Nome do modelo" />
              <button className="botao botao-primario" disabled={processando || !nome.trim()} onClick={() => void salvarNome()}>Salvar nome</button>
              <button className="botao botao-texto" onClick={() => setEditandoNome(false)}>Cancelar</button>
            </div> : <div className="modelo-titulo-editavel">
              <h2 style={{ margin: 0 }}>{template.nome}</h2>
              {editavel && <button className="botao botao-texto" onClick={() => { setNome(template.nome); setEditandoNome(true); }}>Editar nome</button>}
            </div>}
            <p style={{ margin: "4px 0 0", color: "var(--texto-suave)" }}>
              {template.tipo} · versão {template.versao} ·{" "}
              <strong>{RETULO_STATUS[template.status]}</strong>
            </p>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            {template.status === "RASCUNHO" && (
              <button className="botao botao-primario" style={{ marginTop: 0 }} disabled={processando} onClick={() => void publicar()}>
                Publicar
              </button>
            )}
            <button className="botao botao-texto" disabled={processando} onClick={() => void duplicar()}>
              Duplicar
            </button>
          </div>
        </div>

        {erro && (
          <div className="alerta alerta-erro" role="alert" style={{ marginTop: 16 }}>
            {erro}
          </div>
        )}

        <div style={{ marginTop: 24 }}>
          {template.secoes.map((secao) => (
            <SecaoCard key={secao.id} secao={secao} editavel={editavel} aoMudar={carregar} />
          ))}
        </div>

        {editavel && <FormularioSecao templateId={template.id} aoCriar={carregar} />}
      </main>
    </Layout>
  );
}

function SecaoCard({
  secao,
  editavel,
  aoMudar,
}: {
  secao: TemplateSecaoApi;
  editavel: boolean;
  aoMudar: () => void;
}) {
  const [mostrarFormItem, setMostrarFormItem] = useState(false);
  const [editandoTitulo, setEditandoTitulo] = useState(false);
  const [tituloEdit, setTituloEdit] = useState(secao.titulo);
  const [editandoItemId, setEditandoItemId] = useState<string | null>(null);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  async function excluirSecao() {
    await api.excluirSecao(secao.id);
    aoMudar();
  }

  async function salvarTitulo() {
    setErro(null);
    setSalvando(true);
    try {
      await api.editarSecao(secao.id, { titulo: tituloEdit });
      setEditandoTitulo(false);
      aoMudar();
    } catch (e) {
      setErro(e instanceof FalhaApi ? e.message : "Falha ao salvar título");
    } finally {
      setSalvando(false);
    }
  }

  return (
    <div className="cartao" style={{ padding: 16, marginBottom: 16 }}>
      {erro && (
        <div className="alerta alerta-erro" role="alert">
          {erro}
        </div>
      )}

      {editandoTitulo ? (
        <div>
          <label htmlFor={`titulo-edit-${secao.id}`}>Título</label>
          <input
            id={`titulo-edit-${secao.id}`}
            value={tituloEdit}
            onChange={(e) => setTituloEdit(e.target.value)}
            autoFocus
          />
          <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
            <button className="botao botao-primario" style={{ marginTop: 0 }} disabled={salvando} onClick={() => void salvarTitulo()}>
              {salvando ? "Salvando…" : "Salvar"}
            </button>
            <button
              className="botao botao-texto"
              onClick={() => {
                setTituloEdit(secao.titulo);
                setEditandoTitulo(false);
              }}
            >
              Cancelar
            </button>
          </div>
        </div>
      ) : (
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "start" }}>
          <div>
            <strong>{secao.titulo}</strong>
          </div>
          {editavel && (
            <div style={{ display: "flex", gap: 8 }}>
              <button className="botao botao-texto" onClick={() => setEditandoTitulo(true)}>
                Editar
              </button>
              <button className="botao botao-texto" onClick={() => void excluirSecao()}>
                Excluir
              </button>
            </div>
          )}
        </div>
      )}

      <ul style={{ margin: "12px 0 0", paddingLeft: 20 }}>
        {secao.itens.map((item) =>
          editandoItemId === item.id ? (
            <li key={item.id} style={{ listStyle: "none", marginLeft: -20, marginBottom: 8 }}>
              <FormularioItem
                secaoId={secao.id}
                itemExistente={item}
                aoCriar={() => {
                  setEditandoItemId(null);
                  aoMudar();
                }}
                aoCancelar={() => setEditandoItemId(null)}
              />
            </li>
          ) : (
            <li key={item.id} style={{ display: "flex", justifyContent: "space-between", gap: 8, marginBottom: 4 }}>
              <span>
                {item.rotulo}
                <em style={{ color: "var(--texto-suave)" }}> — {RETULO_TIPO_ITEM[item.tipo]}</em>
                {item.opcoes && item.opcoes.length > 0 && (
                  <span style={{ color: "var(--texto-suave)" }}> ({item.opcoes.join(" / ")})</span>
                )}
                <small className="modelo-item-regra">{resumoAvaliacao(item)}</small>
              </span>
              {editavel && (
                <span style={{ display: "flex", gap: 8, flexShrink: 0 }}>
                  <button className="botao botao-texto" onClick={() => setEditandoItemId(item.id)}>
                    Editar
                  </button>
                  <button
                    className="botao botao-texto"
                    onClick={() => void api.excluirItem(item.id).then(aoMudar)}
                  >
                    Excluir
                  </button>
                </span>
              )}
            </li>
          )
        )}
      </ul>

      {editavel &&
        (mostrarFormItem ? (
          <FormularioItem
            secaoId={secao.id}
            aoCriar={() => {
              setMostrarFormItem(false);
              aoMudar();
            }}
            aoCancelar={() => setMostrarFormItem(false)}
          />
        ) : (
          <button className="botao botao-texto" onClick={() => setMostrarFormItem(true)}>
            + Novo item
          </button>
        ))}
    </div>
  );
}

function FormularioSecao({ templateId, aoCriar }: { templateId: string; aoCriar: () => void }) {
  const [aberto, setAberto] = useState(false);
  const [titulo, setTitulo] = useState("");
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  async function aoEnviar(evento: FormEvent) {
    evento.preventDefault();
    setErro(null);
    setSalvando(true);
    try {
      await api.criarSecao(templateId, { titulo });
      setTitulo("");
      setAberto(false);
      aoCriar();
    } catch (e) {
      setErro(e instanceof FalhaApi ? e.message : "Falha ao criar seção");
    } finally {
      setSalvando(false);
    }
  }

  if (!aberto) {
    return (
      <button className="botao botao-primario" onClick={() => setAberto(true)}>
        + Nova seção
      </button>
    );
  }

  return (
    <form className="cartao" style={{ padding: 16 }} onSubmit={aoEnviar}>
      {erro && (
        <div className="alerta alerta-erro" role="alert">
          {erro}
        </div>
      )}
      <label htmlFor="titulo-secao">Título</label>
      <input id="titulo-secao" value={titulo} onChange={(e) => setTitulo(e.target.value)} required />

      <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
        <button className="botao botao-primario" style={{ marginTop: 0 }} type="submit" disabled={salvando}>
          {salvando ? "Salvando…" : "Salvar"}
        </button>
        <button className="botao botao-texto" type="button" onClick={() => setAberto(false)}>
          Cancelar
        </button>
      </div>
    </form>
  );
}

function FormularioItem({
  secaoId,
  itemExistente,
  aoCriar,
  aoCancelar,
}: {
  secaoId: string;
  itemExistente?: TemplateItemApi;
  aoCriar: () => void;
  aoCancelar: () => void;
}) {
  const editando = !!itemExistente;
  const [rotulo, setRotulo] = useState(itemExistente?.rotulo ?? "");
  const [tipo, setTipo] = useState<TipoItemTemplate>(itemExistente?.tipo ?? "CHECKBOX_SIMPLES");
  const [opcoesTexto, setOpcoesTexto] = useState(itemExistente?.opcoes?.join(", ") ?? "");
  const [modoAvaliacao, setModoAvaliacao] = useState(itemExistente?.avaliacao?.modo ?? (itemExistente?.tipo === "CONFORME" ? "PADRAO" : "PADRAO"));
  const [valoresProblema, setValoresProblema] = useState<string[]>(itemExistente?.avaliacao?.valoresProblema ?? []);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  async function aoEnviar(evento: FormEvent) {
    evento.preventDefault();
    setErro(null);
    setSalvando(true);
    try {
      const opcoes =
        tipo === "STATUS_MULTIPLO"
          ? opcoesTexto.split(",").map((o) => o.trim()).filter(Boolean)
          : undefined;
      const avaliacao = modoAvaliacao === "PADRAO" ? null : { modo: modoAvaliacao, valoresProblema: modoAvaliacao === "OPCOES" ? valoresProblema : undefined };
      if (editando) {
        await api.editarItem(itemExistente.id, { rotulo, tipo, opcoes, avaliacao });
      } else {
        await api.criarItem(secaoId, { rotulo, tipo, opcoes, avaliacao });
      }
      aoCriar();
    } catch (e) {
      setErro(e instanceof FalhaApi ? e.message : `Falha ao ${editando ? "salvar" : "criar"} item`);
    } finally {
      setSalvando(false);
    }
  }

  return (
    <form onSubmit={aoEnviar} style={{ marginTop: 12 }}>
      {erro && (
        <div className="alerta alerta-erro" role="alert">
          {erro}
        </div>
      )}
      <label htmlFor={`rotulo-item-${secaoId}`}>Rótulo</label>
      <input id={`rotulo-item-${secaoId}`} value={rotulo} onChange={(e) => setRotulo(e.target.value)} required />

      <label htmlFor={`tipo-item-${secaoId}`}>Tipo</label>
      <select
        id={`tipo-item-${secaoId}`}
        value={tipo}
        onChange={(e) => setTipo(e.target.value as TipoItemTemplate)}
      >
        <option value="CHECKBOX_SIMPLES">Checkbox simples</option>
        <option value="STATUS_MULTIPLO">Status (múltipla escolha)</option>
        <option value="TEXTO_LIVRE">Texto livre</option>
        <option value="SIM_NAO">Sim e Não</option>
        <option value="CONFORME">Conforme / Não conforme / N.A.</option>
      </select>

      {tipo === "STATUS_MULTIPLO" && (
        <>
          <label htmlFor={`opcoes-item-${secaoId}`}>Opções (separadas por vírgula)</label>
          <input
            id={`opcoes-item-${secaoId}`}
            value={opcoesTexto}
            onChange={(e) => setOpcoesTexto(e.target.value)}
            placeholder="Funcionando normalmente, Com falha"
            required
          />
        </>
      )}

      <fieldset className="modelo-avaliacao">
        <legend>Quando esta resposta vira uma pendência?</legend>
        <p>Escolha uma regra apenas se este item puder indicar um problema.</p>
        <select
          value={modoAvaliacao}
          onChange={(e) => { setModoAvaliacao(e.target.value as typeof modoAvaliacao); setValoresProblema([]); }}
        >
          <option value="PADRAO">Não identificar automaticamente</option>
          {(tipo === "STATUS_MULTIPLO" || tipo === "SIM_NAO" || tipo === "CONFORME" || tipo === "CHECKBOX_SIMPLES") && <option value="OPCOES">Pela resposta escolhida</option>}
          <option value="MARCACAO_MANUAL">O vistoriador decide</option>
        </select>

        {modoAvaliacao === "OPCOES" && (
          <div className="modelo-opcoes-avaliacao">
            <span>Marque o que significa problema</span>
            {(tipo === "CHECKBOX_SIMPLES"
              ? ["Marcado", "Desmarcado"]
              : tipo === "SIM_NAO"
                ? ["Sim", "Não"]
                : tipo === "CONFORME"
                  ? ["Conforme", "Não conforme", "N.A."]
                  : opcoesTexto.split(",").map((o) => o.trim()).filter(Boolean)
            ).map((opcao) => (
              <label key={opcao} className={valoresProblema.includes(opcao) ? "problema" : "normal"}>
                <input
                  type="checkbox"
                  checked={valoresProblema.includes(opcao)}
                  onChange={(e) => setValoresProblema((atuais) => e.target.checked ? [...atuais, opcao] : atuais.filter((v) => v !== opcao))}
                />
                <span>{opcao}</span>
                <em>{valoresProblema.includes(opcao) ? "Problema" : "Normal"}</em>
              </label>
            ))}
          </div>
        )}
        {modoAvaliacao === "MARCACAO_MANUAL" && <div className="modelo-avaliacao-dica">Na vistoria, aparecerá a opção “Registrar como problema” abaixo deste item.</div>}
      </fieldset>

      <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
        <button className="botao botao-primario" style={{ marginTop: 0 }} type="submit" disabled={salvando}>
          {salvando ? "Salvando…" : "Salvar"}
        </button>
        <button className="botao botao-texto" type="button" onClick={aoCancelar}>
          Cancelar
        </button>
      </div>
    </form>
  );
}
